import { Badge, Card, CardHeader, CardTitle, Pagination } from '@/shared/components';
import type { PaginationMeta, TemplateVersionStatus, TemplateVersionSummary } from '../../types/documentTemplates.types';
import { STATUS_BADGE_VARIANTS, STATUS_LABELS, versionActivity } from '../../utils/versionLabels';

interface Props {
  versions: TemplateVersionSummary[];
  meta: PaginationMeta;
  selectedId: string | null;
  onSelect: (versionId: string) => void;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
}

const DOT_CLASSES: Record<TemplateVersionStatus, string> = {
  draft: 'border-2 border-mp-warning',
  published: 'bg-mp-success',
  superseded: 'bg-mp-input',
};

function VersionListItem({ version, isSelected, onSelect }: { version: TemplateVersionSummary; isSelected: boolean; onSelect: Props['onSelect'] }) {
  return (
    <li>
      <button
        type="button"
        aria-current={isSelected ? 'true' : undefined}
        onClick={() => onSelect(version.id)}
        className={`grid w-full grid-cols-[1rem_minmax(0,1fr)] gap-3 rounded-lg p-3 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mp-primary ${
          isSelected ? 'bg-mp-primary-content' : 'hover:bg-mp-background'
        }`}
      >
        <span aria-hidden="true" className={`mt-1.5 size-2.5 rounded-full ${DOT_CLASSES[version.status]}`} />
        <span className="min-w-0 text-sm">
          <span className="flex items-center gap-2">
            <span className="font-semibold">v{version.versionNumber}</span>{' '}
            <Badge variant={STATUS_BADGE_VARIANTS[version.status]} size="xs">
              {STATUS_LABELS[version.status]}
            </Badge>
          </span>{' '}
          {version.status !== 'draft' && (
            <span className={`mt-1 block break-words ${version.changeNote ? '' : 'text-mp-muted'}`}>
              {version.changeNote || 'No change note'}
            </span>
          )}{' '}
          <span className="mt-0.5 block text-mp-muted">{versionActivity(version)}</span>
        </span>
      </button>
    </li>
  );
}

export function VersionList({ versions, meta, selectedId, onSelect, onPageChange, onPerPageChange }: Props) {
  return (
    <Card ariaLabelledBy="version-history-heading">
      <CardHeader>
        <CardTitle id="version-history-heading">
          History
        </CardTitle>
        <p className="mt-0.5 text-sm text-mp-muted">
          {meta.total} {meta.total === 1 ? 'version' : 'versions'}
        </p>
      </CardHeader>
      <ol className="space-y-0.5 p-2">
        {versions.map((version) => (
          <VersionListItem key={version.id} version={version} isSelected={version.id === selectedId} onSelect={onSelect} />
        ))}
      </ol>
      {meta.lastPage > 1 && (
        <div className="border-t border-mp-border px-4 py-3">
          <Pagination
            currentPage={meta.currentPage}
            lastPage={meta.lastPage}
            total={meta.total}
            perPage={meta.perPage}
            from={meta.from}
            to={meta.to}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
          />
        </div>
      )}
    </Card>
  );
}
